import { useSessionStorage } from '@vueuse/core'
import type { Ref } from 'vue'
import { toRaw } from 'vue'
import { uniq } from 'lodash-es'

export enum LogLevel {
  Trace = 'TRACE',
  Debug = 'DEBUG',
  Info = 'INFO',
  Warn = 'WARN',
  Error = 'ERROR'
}

export interface Logger {
  get name(): string

  setLevel: (level: LogLevel | string, enable: boolean) => void

  setMinimumLevel: (level: LogLevel | string) => void

  resetLevels: () => void

  trace: (message: string, ...data: unknown[]) => void

  debug: (message: string, ...data: unknown[]) => void

  info: (message: string, ...data: unknown[]) => void

  warn: (message: string, ...data: unknown[]) => void

  error: (message: string | Error, ...data: unknown[]) => void

  log: (level: LogLevel, message: string | Error, ...data: unknown[]) => void

  levels: Map<LogLevel, Ref<boolean>>
}

export interface LoggerConfig {
  trace: boolean
  debug: boolean
  info: boolean
  warn: boolean
  error: boolean
}

const ALL_LOGGERS: Logger[] = []

const OVERRIDES = {
  Intlify: {
    [LogLevel.Warn]: false
  }
}

const DEFAULT_LEVEL: Map<LogLevel, Ref<boolean>> = new Map<LogLevel, Ref<boolean>>([
  // default logger settings
  [LogLevel.Trace, useSessionStorage<boolean>('custom-loglevel-default-trace', false, { listenToStorageChanges: true })],
  [LogLevel.Debug, useSessionStorage<boolean>('custom-loglevel-default-debug', false, { listenToStorageChanges: true })],
  [LogLevel.Info, useSessionStorage<boolean>('custom-loglevel-default-info', false, { listenToStorageChanges: true })],
  [LogLevel.Warn, useSessionStorage<boolean>('custom-loglevel-default-warn', true, { listenToStorageChanges: true })],
  [LogLevel.Error, useSessionStorage<boolean>('custom-loglevel-default-error', true, { listenToStorageChanges: true })]
])

export function getDefaultLevel(): LoggerConfig {
  const cpy: LoggerConfig = { trace: false, debug: false, info: false, warn: false, error: false }
  DEFAULT_LEVEL.forEach((value, key) => {
    cpy[key.toLowerCase()] = toRaw(value.value)
  })
  return readonly(cpy)
}

export function getKnownLoggers(): string[] {
  const keysFromStore = [
    ...Object.keys(sessionStorage)
      .filter((s) => s.startsWith('custom-loglevel-'))
      .map((s) => s.substring(0, s.lastIndexOf('-')))
      .map((s) => s.substring(s.lastIndexOf('-') + 1)),
    ...ALL_LOGGERS.map((value: Logger) => value.name)
  ]
  return uniq(keysFromStore)
}

export function getLogger(logger: string): Logger | undefined {
  return ALL_LOGGERS.find((value: Logger) => value.name.toLowerCase() == logger.toLowerCase())
}

export function setLevel(level: LogLevel | string, enable: boolean, includeLogger = true): void {
  const lvl = string2level(level)
  if (DEFAULT_LEVEL.has(lvl)) {
    console.log(`Logger level [${lvl}] ${enable ? 'enabled' : 'disabled'}`)
    const level = DEFAULT_LEVEL.get(lvl)
    if (level) {
      level.value = enable
    }
    if (includeLogger) for (const logger of ALL_LOGGERS) logger.setLevel(lvl, enable)
  }
}

export function resetLevels(): void {
  setLevel(LogLevel.Trace, false)
  setLevel(LogLevel.Debug, false)
  setLevel(LogLevel.Info, false)
  setLevel(LogLevel.Warn, true)
  setLevel(LogLevel.Error, true)
  for (const logger of ALL_LOGGERS) {
    logger.resetLevels()
  }
}

export function setMinimumLevel(level: LogLevel | string): void {
  switch (string2level(level)) {
    case LogLevel.Trace:
      setLevel(LogLevel.Trace, true)
      setLevel(LogLevel.Debug, true)
      setLevel(LogLevel.Info, true)
      setLevel(LogLevel.Warn, true)
      setLevel(LogLevel.Error, true)
      break
    case LogLevel.Debug:
      setLevel(LogLevel.Trace, false)
      setLevel(LogLevel.Debug, true)
      setLevel(LogLevel.Info, true)
      setLevel(LogLevel.Warn, true)
      setLevel(LogLevel.Error, true)
      break
    case LogLevel.Info:
      setLevel(LogLevel.Trace, false)
      setLevel(LogLevel.Debug, false)
      setLevel(LogLevel.Info, true)
      setLevel(LogLevel.Warn, true)
      setLevel(LogLevel.Error, true)
      break
    case LogLevel.Warn:
      setLevel(LogLevel.Trace, false)
      setLevel(LogLevel.Debug, false)
      setLevel(LogLevel.Info, false)
      setLevel(LogLevel.Warn, true)
      setLevel(LogLevel.Error, true)
      break
    case LogLevel.Error:
      setLevel(LogLevel.Trace, false)
      setLevel(LogLevel.Debug, false)
      setLevel(LogLevel.Info, false)
      setLevel(LogLevel.Warn, false)
      setLevel(LogLevel.Error, true)
      break
  }
}

function string2level(level: LogLevel | string): LogLevel {
  let levelMember: LogLevel | undefined
  if (typeof level == 'string') {
    levelMember = level.toUpperCase() as LogLevel | undefined
    if (!levelMember) throw new Error('level must be one of trace,debug,info,warn,error')
  } else {
    levelMember = level
  }
  return levelMember
}

export function useLogger(prefix: string): Logger {
  const existing = getLogger(prefix)
  if (existing) return existing
  const $prefix: string = prefix
  const logLevels: Map<LogLevel, Ref<boolean>> = new Map<LogLevel, Ref<boolean>>()
  resetLevels()

  function setLevel(level: LogLevel | string, enable: boolean): void {
    let levelMember: LogLevel | undefined
    if (typeof level == 'string') {
      levelMember = level.toUpperCase() as LogLevel | undefined
      if (!levelMember) throw new Error('level must be one of trace,debug,info,warn,error')
    } else {
      levelMember = level
    }

    if (logLevels.has(levelMember)) {
      console.log(`Logger [${$prefix}] level [${level}] ${enable ? 'enabled' : 'disabled'}`)
      const levelRef = logLevels.get(levelMember)
      if (levelRef) levelRef.value = enable
    }
  }

  function resetLevels(): void {
    for (const l of [LogLevel.Trace, LogLevel.Debug, LogLevel.Info, LogLevel.Warn, LogLevel.Error]) {
      const defaultEnable = (OVERRIDES[$prefix]?.[l] as boolean) ?? (DEFAULT_LEVEL.get(LogLevel.Trace)?.value as boolean) ?? false
      if (!logLevels.has(l)) {
        const s = useSessionStorage<boolean>(`custom-loglevel-${$prefix}-${l.toLowerCase()}`, defaultEnable, {
          listenToStorageChanges: true,
          writeDefaults: true
        })
        logLevels.set(l, s)
      } else {
        setLevel(l, defaultEnable)
      }
    }
  }

  function setMinimumLevel(level: LogLevel | string): void {
    let levelMember: LogLevel | undefined
    if (typeof level == 'string') {
      levelMember = level.toUpperCase() as LogLevel | undefined
      if (!levelMember) throw new Error('level must be one of trace,debug,info,warn,error')
    } else {
      levelMember = level
    }
    for (const l of logLevels.keys()) setLevel(l, false)
    // noinspection FallThroughInSwitchStatementJS
    switch (levelMember) {
      case LogLevel.Trace:
        setLevel(LogLevel.Trace, true)
        setLevel(LogLevel.Debug, true)
        setLevel(LogLevel.Info, true)
        setLevel(LogLevel.Warn, true)
        setLevel(LogLevel.Error, true)
        break
      case LogLevel.Debug:
        setLevel(LogLevel.Trace, false)
        setLevel(LogLevel.Debug, true)
        setLevel(LogLevel.Info, true)
        setLevel(LogLevel.Warn, true)
        setLevel(LogLevel.Error, true)
        break
      case LogLevel.Info:
        setLevel(LogLevel.Trace, false)
        setLevel(LogLevel.Debug, false)
        setLevel(LogLevel.Info, true)
        setLevel(LogLevel.Warn, true)
        setLevel(LogLevel.Error, true)
        break
      case LogLevel.Warn:
        setLevel(LogLevel.Trace, false)
        setLevel(LogLevel.Debug, false)
        setLevel(LogLevel.Info, false)
        setLevel(LogLevel.Warn, true)
        setLevel(LogLevel.Error, true)
        break
      case LogLevel.Error:
        setLevel(LogLevel.Trace, false)
        setLevel(LogLevel.Debug, false)
        setLevel(LogLevel.Info, false)
        setLevel(LogLevel.Warn, false)
        setLevel(LogLevel.Error, true)
        break
    }
  }

  function trace(message: string, ...data: unknown[]): void {
    log(LogLevel.Trace, message, ...data)
  }

  function debug(message: string, ...data: unknown[]): void {
    log(LogLevel.Debug, message, ...data)
  }

  function info(message: string, ...data: unknown[]): void {
    log(LogLevel.Info, message, ...data)
  }

  function warn(message: string, ...data: unknown[]): void {
    log(LogLevel.Warn, message, ...data)
  }

  function error(message: string | Error, ...data: unknown[]): void {
    log(LogLevel.Error, message, ...data)
  }

  /**
   *
   * @param {LogLevel} level the log level from LogLevel
   * @param {String} message
   * @param {unknown[]} data
   */
  function log(level: LogLevel, message: string | Error, ...data: unknown[]) {
    if (!logLevels.get(level)?.value) return
    const optionalData: unknown[] | undefined = data.length > 0 ? data : undefined
    const newMessage = `[${level.toUpperCase()}] [${$prefix}] ${message}`
    switch (level) {
      case LogLevel.Trace:
        if (optionalData) console.trace(newMessage, ...optionalData)
        else console.trace(newMessage)
        break
      case LogLevel.Debug:
        if (optionalData) console.debug(newMessage, ...optionalData)
        else console.debug(newMessage)
        break
      case LogLevel.Warn:
        if (optionalData) console.warn(newMessage, ...optionalData)
        else console.warn(newMessage)
        break
      case LogLevel.Error:
        if (optionalData) console.error(newMessage, ...optionalData)
        else console.error(newMessage)
        break
      case LogLevel.Info:
      default:
        if (optionalData) console.info(newMessage, ...optionalData)
        else console.info(newMessage)
        break
    }
  }
  const logger: Logger = {
    name: $prefix,
    levels: logLevels,
    resetLevels,
    setMinimumLevel,
    log,
    trace,
    debug,
    info,
    warn,
    error,
    setLevel
  }
  ALL_LOGGERS.push(logger)
  return logger
}
